import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import type { ReactLocalization } from "@fluent/react";

export type UpsertInternationalTemplateFormFields = {
  recipientId: API.RecipientId;
  name: string;
  fromAccountId: API.AccountId;
  wireReason?: string;
  wireReasonDescription?: string;
  amount?: number;
  recipientCurrency?: string;
  amountCurrency?: string;
  memo?: string;
  note?: string;
};

function createSchema(l10n?: ReactLocalization) {
  const required =
    l10n?.getString("form-generic-validation-required") || "Required";

  return z.object({
    recipientId: z.string().min(1, { message: required }),
    name: z.string().min(1, { message: required }),
    fromAccountId: z.string().min(1, { message: required }),
    wireReason: z.string().optional(),
    wireReasonDescription: z.string().optional(),
    amount: z.preprocess(
      (val) => {
        if (val === "" || val === undefined || val === null) return undefined;
        if (typeof val === "string" || typeof val === "number") {
          const num = Number(val);
          return Number.isNaN(num) ? undefined : num;
        }
        return val;
      },
      z
        .number()
        .refine(
          (val) => {
            const parts = String(val).split(".");
            return !parts[1] || parts[1].length <= 3;
          },
          { message: "Maximum 3 decimal places" },
        )
        .optional(),
    ),
    recipientCurrency: z.string().optional(),
    amountCurrency: z.string().optional(),
    memo: z.string().max(128, { message: "Maximum 128 characters" }).optional(),
    note: z.string().optional(),
  });
}

export function createDefaultValues(): UpsertInternationalTemplateFormFields {
  return {
    recipientId: "" as API.RecipientId,
    name: "",
    fromAccountId: "" as API.AccountId,
    wireReason: undefined,
    wireReasonDescription: undefined,
    amount: undefined,
    recipientCurrency: undefined,
    amountCurrency: undefined,
    memo: undefined,
    note: undefined,
  };
}

export const transformApiSwiftWireTemplateToForm = (
  template: API.SwiftWireTemplate,
): UpsertInternationalTemplateFormFields => ({
  recipientId: template.recipient,
  fromAccountId: template.from_account,
  name: template.name,
  amount: template.amount || undefined,
  wireReason: template.wire_reason || undefined,
  recipientCurrency: template.recipient_currency || undefined,
  amountCurrency: template.amount_currency || undefined,
  memo: template.memo || undefined,
  note: template.note || undefined,
});

export const useUpsertInternationalTemplateForm = (
  l10n?: ReactLocalization,
) => {
  const schema = createSchema(l10n);
  return useForm<UpsertInternationalTemplateFormFields>({
    defaultValues: createDefaultValues(),
    resolver: zodResolver(schema),
  });
};
